import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cover from "../components/BookCover";
import { fetchProfile, initiateCCAvenue, validateCoupon } from "../api";
import { getStoredAuthToken } from "../authStorage";

export default function CheckoutPage({ cart }) {
    const navigate = useNavigate();
    const ccavFormRef = useRef(null);
    const [step, setStep] = useState(1);
    const [placing, setPlacing] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponState, setCouponState] = useState({ loading: false, message: "", error: "" });
    const [form, setForm] = useState({ name: "", email: "", phone: "", street: "", city: "", state: "", pincode: "" });

    const book = cart[0] || null;
    const subtotal = cart.reduce((sum, item) => sum + Number(item.price || 0), 0);
    const discountAmount = Number(appliedCoupon?.discountAmount || 0);
    const total = Math.max(0, subtotal - discountAmount);
    const fmtINR = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    useEffect(() => {
        const applicableBookId = appliedCoupon?.coupon?.applicableBookId;
        const isSpecificBookCouponInvalid = applicableBookId
            && (cart.length !== 1 || String(cart[0]?._id) !== String(applicableBookId));

        if (isSpecificBookCouponInvalid) {
            setAppliedCoupon(null);
            setCouponState({
                loading: false,
                message: "",
                error: "This coupon is for a specific book. Please keep only that book in your cart to use it.",
            });
        }
    }, [cart, appliedCoupon]);

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const token = getStoredAuthToken();
                if (!token) {
                    navigate("/signin");
                    return;
                }

                const profile = await fetchProfile(token);
                if (profile.addresses?.length) {
                    setAddresses(profile.addresses);
                    const defaultAddress = profile.addresses.find((address) => address.isDefault) || profile.addresses[0];
                    setSelectedAddressId(defaultAddress._id || defaultAddress.id);
                } else {
                    setShowAddressForm(true);
                }

                setForm((current) => ({
                    ...current,
                    name: profile.name || "",
                    email: profile.email || "",
                }));
            } catch {
                setShowAddressForm(true);
            } finally {
                setLoadingProfile(false);
            }
        };

        loadProfile();
    }, [navigate]);

    const updateForm = (key) => (event) => {
        setForm((current) => ({ ...current, [key]: event.target.value }));
    };

    const handleCouponChange = (event) => {
        setCouponCode(event.target.value.toUpperCase());
        setAppliedCoupon(null);
        setCouponState({ loading: false, message: "", error: "" });
    };

    const handleApplyCoupon = async () => {
        const token = getStoredAuthToken();

        if (!token) {
            navigate("/signin");
            return;
        }

        if (!book) {
            setCouponState({ loading: false, message: "", error: "Add a book before using a coupon." });
            return;
        }

        if (!couponCode.trim()) {
            setCouponState({ loading: false, message: "", error: "Enter a coupon code first." });
            return;
        }

        setCouponState({ loading: true, message: "", error: "" });
        try {
            const result = await validateCoupon(token, {
                couponCode: couponCode.trim(),
                orderItems: cart.map((item) => ({
                    book: item._id,
                    qty: 1,
                })),
            });
            setAppliedCoupon(result);
            setCouponState({ loading: false, message: `Coupon applied. You saved ${fmtINR(result.discountAmount)}.`, error: "" });
        } catch (error) {
            setAppliedCoupon(null);
            setCouponState({ loading: false, message: "", error: error.message || "Unable to apply coupon." });
        }
    };

    const resolveShippingAddress = () => {
        if (showAddressForm) {
            if (!form.street || !form.city || !form.state || !form.pincode || !form.phone) {
                throw new Error("Please fill in all address fields including phone number.");
            }

            return {
                address: {
                    street: form.street,
                    city: form.city,
                    state: form.state,
                    zipCode: form.pincode,
                    country: "India",
                },
                phone: form.phone,
            };
        }

        const picked = addresses.find((address) => (address._id || address.id) === selectedAddressId);
        if (!picked) {
            throw new Error("Please select a delivery address.");
        }

        return {
            address: {
                street: picked.street || picked.address || "",
                city: picked.city || "",
                state: picked.state || "",
                zipCode: picked.zipCode || picked.pincode || "",
                country: picked.country || "India",
            },
            phone: picked.phone || form.phone,
        };
    };

    const handleCCAvenuePay = async () => {
        setPlacing(true);
        try {
            const token = getStoredAuthToken();
            if (!token) {
                navigate("/signin");
                return;
            }

            const { address, phone } = resolveShippingAddress();
            let customerName = form.name;
            if (!showAddressForm && !customerName) {
                const picked = addresses.find((addr) => (addr._id || addr.id) === selectedAddressId);
                if (picked) customerName = picked.fullName || picked.name || "";
            }

            if (!phone) {
                throw new Error("Please provide a phone number for delivery.");
            }
            if (!/^\d{10}$/.test(String(phone).trim())) {
                throw new Error("Please enter a valid 10-digit phone number for delivery.");
            }
            if (!/^\d{6}$/.test(String(address.zipCode).trim())) {
                throw new Error("Please ensure the delivery address has a valid 6-digit pincode.");
            }
            if (!customerName || !customerName.trim()) {
                throw new Error("Please provide your full name.");
            }
            if (!form.email || !form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
                throw new Error("Please provide a valid email address. Make sure your profile has an email.");
            }

            const applicableBookId = appliedCoupon?.coupon?.applicableBookId;
            if (applicableBookId && (cart.length !== 1 || String(cart[0]?._id) !== String(applicableBookId))) {
                throw new Error("This coupon is for a specific book. Please keep only that book in your cart to use it.");
            }

            const ccavData = await initiateCCAvenue(token, {
                orderItems: cart.map((item) => ({
                    title: item.title,
                    qty: 1,
                    image: item.coverImage || item.img || "",
                    price: item.price,
                    book: item._id,
                })),
                shippingAddress: address,
                itemsPrice: subtotal,
                shippingPrice: 0,
                totalPrice: total,
                customerName: customerName.trim(),
                customerEmail: form.email,
                customerPhone: phone,
                couponCode: appliedCoupon?.coupon?.code || "",
            });

            const formEl = ccavFormRef.current;
            if (formEl) {
                formEl.action = ccavData.ccavenueUrl;
                formEl.querySelector('input[name="encRequest"]').value = ccavData.encRequest;
                formEl.querySelector('input[name="access_code"]').value = ccavData.accessCode;
                formEl.submit();
            }
        } catch (error) {
            alert(error.message || "Failed to initiate payment.");
        } finally {
            setPlacing(false);
        }
    };

    if (cart.length === 0) {
        return (
            <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#FDF5F5", padding: 24 }}>
                <div style={{ maxWidth: 520, background: "white", border: "1px solid #f0d0c8", borderRadius: 20, padding: 28, textAlign: "center" }}>
                    <h1 style={{ marginTop: 0, color: "#1a0a08" }}>Your Cart Is Empty</h1>
                    <p style={{ color: "#7a5550", lineHeight: 1.7 }}>Add at least one book to continue to checkout.</p>
                    <button onClick={() => navigate("/cart")} style={{ padding: "12px 20px", borderRadius: 10, border: "none", background: "#C0392B", color: "white", fontWeight: 700, cursor: "pointer" }}>
                        Return to Cart
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh", background: "#FDF5F5", padding: "32px 20px" }}>
            <style>{`
                @media (max-width: 900px) {
                    .checkout-layout {
                        grid-template-columns: 1fr !important;
                    }
                }
                @media (max-width: 640px) {
                    .checkout-form-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
            <div className="checkout-layout" style={{ maxWidth: 1080, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: 24 }}>
                <div style={{ background: "white", border: "1px solid #f0d0c8", borderRadius: 22, padding: 28 }}>
                    <h1 style={{ marginTop: 0, color: "#1a0a08" }}>Checkout</h1>
                    <p style={{ color: "#7a5550", marginBottom: 24 }}>Cart-wide coupons can cover multiple books. Book-specific coupons need only that book in the cart.</p>

                    <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
                        {[1, 2].map((value) => (
                            <button
                                key={value}
                                onClick={() => setStep(value)}
                                style={{
                                    padding: "10px 14px",
                                    borderRadius: 999,
                                    border: value === step ? "none" : "1px solid #f0d0c8",
                                    background: value === step ? "#C0392B" : "white",
                                    color: value === step ? "white" : "#8B1A1A",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                            >
                                {value === 1 ? "1. Address" : "2. Payment"}
                            </button>
                        ))}
                    </div>

                    {loadingProfile ? (
                        <p style={{ color: "#7a5550" }}>Loading your profile...</p>
                    ) : step === 1 ? (
                        <>
                            {addresses.length > 0 && !showAddressForm ? (
                                <div style={{ display: "grid", gap: 12, marginBottom: 20 }}>
                                    {addresses.map((address) => (
                                        <button
                                            key={address._id || address.id}
                                            onClick={() => setSelectedAddressId(address._id || address.id)}
                                            style={{
                                                textAlign: "left",
                                                padding: 16,
                                                borderRadius: 14,
                                                border: selectedAddressId === (address._id || address.id) ? "2px solid #C0392B" : "1px solid #f0d0c8",
                                                background: "white",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <strong>{address.name || "Address"}</strong>
                                            <div style={{ color: "#7a5550", marginTop: 6, lineHeight: 1.6 }}>
                                                {address.address || address.street}
                                                <br />
                                                {address.city}, {address.state} {address.pincode || address.zipCode}
                                                <br />
                                                {address.phone || "No phone saved"}
                                            </div>
                                        </button>
                                    ))}
                                    <button onClick={() => setShowAddressForm(true)} style={{ padding: "12px 14px", borderRadius: 10, border: "1px dashed #f0d0c8", background: "transparent", color: "#8B1A1A", fontWeight: 700, cursor: "pointer" }}>
                                        Add New Address
                                    </button>
                                </div>
                            ) : null}

                            {showAddressForm ? (
                                <div className="checkout-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                    {[
                                        ["name", "Full Name"],
                                        ["phone", "Phone Number"],
                                        ["street", "Street Address"],
                                        ["city", "City"],
                                        ["state", "State"],
                                        ["pincode", "Pin Code"],
                                    ].map(([key, label]) => (
                                        <label key={key} style={{ display: "grid", gap: 6 }}>
                                            <span style={{ fontWeight: 700, color: "#7a5550" }}>{label}</span>
                                            <input value={form[key]} onChange={updateForm(key)} style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid #f0d0c8" }} />
                                        </label>
                                    ))}
                                </div>
                            ) : null}

                            <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
                                {showAddressForm && addresses.length > 0 ? (
                                    <button onClick={() => setShowAddressForm(false)} style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid #f0d0c8", background: "white", color: "#8B1A1A", fontWeight: 700, cursor: "pointer" }}>
                                        Use Saved Address
                                    </button>
                                ) : null}
                                <button onClick={() => setStep(2)} style={{ padding: "12px 18px", borderRadius: 10, border: "none", background: "#C0392B", color: "white", fontWeight: 700, cursor: "pointer" }}>
                                    Continue to Payment
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ border: "1px solid #f0d0c8", borderRadius: 16, padding: 18, marginBottom: 20, background: "#fffaf8" }}>
                                <div style={{ fontWeight: 800, color: "#8B1A1A", marginBottom: 10 }}>Apply Coupon</div>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <input value={couponCode} onChange={handleCouponChange} placeholder="SAVE500" style={{ flex: "1 1 220px", padding: "12px 14px", borderRadius: 10, border: "1px solid #f0d0c8" }} />
                                    <button onClick={handleApplyCoupon} disabled={couponState.loading || cart.length === 0} style={{ padding: "12px 16px", borderRadius: 10, border: "none", background: cart.length === 0 ? "#d7a3a3" : "#C0392B", color: "white", fontWeight: 700, cursor: cart.length === 0 ? "not-allowed" : "pointer" }}>
                                        {couponState.loading ? "Checking..." : "Apply"}
                                    </button>
                                </div>
                                {cart.length > 1 ? <div style={{ marginTop: 10, color: "#7a5550", fontSize: 13 }}>If this code is for one book, keep only that book in your cart before applying it.</div> : null}
                                {couponState.message ? <div style={{ marginTop: 10, color: "#1e8449", fontWeight: 700 }}>{couponState.message}</div> : null}
                                {couponState.error ? <div style={{ marginTop: 10, color: "#c62828", fontWeight: 700 }}>{couponState.error}</div> : null}
                            </div>

                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button onClick={() => setStep(1)} style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid #f0d0c8", background: "white", color: "#8B1A1A", fontWeight: 700, cursor: "pointer" }}>
                                    Back
                                </button>
                                <button onClick={handleCCAvenuePay} disabled={placing} style={{ padding: "12px 18px", borderRadius: 10, border: "none", background: "#C0392B", color: "white", fontWeight: 700, cursor: "pointer" }}>
                                    {placing ? "Redirecting..." : `Pay ${fmtINR(total)}`}
                                </button>
                            </div>
                        </>
                    )}

                    <form ref={ccavFormRef} method="POST" style={{ display: "none" }}>
                        <input type="hidden" name="encRequest" value="" />
                        <input type="hidden" name="access_code" value="" />
                    </form>
                </div>

                <div style={{ background: "white", border: "1px solid #f0d0c8", borderRadius: 22, padding: 24, height: "fit-content" }}>
                    <h2 style={{ marginTop: 0, fontSize: 20 }}>Order Summary</h2>
                    <div style={{ display: "grid", gap: 12, marginBottom: 18 }}>
                        {cart.map((item) => (
                            <div key={item._id} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                <Cover book={item} w={56} h={78} shadow={false} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: "#1a0a08" }}>{item.title}</div>
                                    <div style={{ color: "#7a5550", fontSize: 13 }}>{item.author}</div>
                                </div>
                                <strong style={{ color: "#C0392B" }}>{fmtINR(item.price)}</strong>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#7a5550" }}>
                            <span>Subtotal</span>
                            <span>{fmtINR(subtotal)}</span>
                        </div>
                        {discountAmount > 0 ? (
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#1e8449", fontWeight: 700 }}>
                                <span>Coupon Discount</span>
                                <span>-{fmtINR(discountAmount)}</span>
                            </div>
                        ) : null}
                        <div style={{ display: "flex", justifyContent: "space-between", color: "#1e8449", fontWeight: 700 }}>
                            <span>Delivery</span>
                            <span>FREE</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0d0c8", fontWeight: 800, fontSize: 16 }}>
                            <span>Total</span>
                            <span style={{ color: "#C0392B" }}>{fmtINR(total)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
